import { useCallback, useEffect, useRef, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { Recurso } from '../../api/ops/pagos';
import type { ContenidoPlano, Decoracion, FechaDeEfecto, ImpactoPlano, Plano as PlanoApi, Posicion, ResumenVersion, VersionPlano } from '../../api/ops/sala';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { rolesDe } from '../../logica/acceso';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera, type TarjetaAyuda } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import { todas, useContextoReservas, type ContextoReservas } from '../reservas/contexto';
import { diaCorto, diaLargo, horaEn, hoyEn, sumarDias } from '../reservas/fechas';
import { conArticulo, mayuscula } from '../reservas/textos';
import type { PropsPantalla } from '../tipos';
import { MuestraPieza } from './CapaDecoracion';
import {
    AVISO_CIERRALA_YA, AVISO_CIERRE_LLENO, AVISO_LIMITE, AVISO_PUNTOS, EJEMPLO_NOMBRE, MAX_PIEZAS, MAX_PUNTOS, MAX_TEXTO, NOMBRE_TIPO, PASO_GIRO, PASO_REJILLA, SOLO_DECORACION, TIPOS, ajustar,
    cabeOtroPunto, cambiarTipo, cambiosDeDecoracion, claveDeco, conCierre, conNombre, duplicarPieza, errorDeNombre, estaCerrada, grados, indiceDeClave,
    guiaPared, lineasDeCambios, miles, moverPieza, notaPared, pared as paredNueva, piezaNueva, puntosAbiertos, puntosDe, sePuedeCerrar, textoPuntos, type CambiosDecoracion, type Punto, type TipoPieza,
} from './decoracion';
import { choquesDePieza, choquesDeRecurso, huecoParaPieza, huecoParaRecurso, queSePisa, type Escena } from './huella';
import { Lienzo, type Arrastre } from './Lienzo';
import '../reservas/reservas.css';
import '../sala/sala.css';
import './plano.css';

/**
 * 8.6 Editor de plano (Configuración, entrada `plano`, bloque Plano y sala; G401, G402, D-066): Dibujo · Versiones.
 *
 * - Dibujo: la versión en vigor (`getFloorPlanVersion`), la programada (con «Retirar», motivo) y el borrador único del
 *   plano (G402: `openFloorPlanDraft` o `getFloorPlanDraft`). En el editor se eligen y se arrastran los recursos, se
 *   cambian su forma, posición, tamaño y giro, se colocan los que aún no están y se quitan del plano (siguen en Recursos);
 *   se añaden salas. Cada cambio se guarda entero con `If-Match` (`saveFloorPlanDraft`); si otro guardó antes (`412`), aviso
 *   de «versión superada» y se vuelve a cargar. Los recursos nuevos se crean en 8.5 (punto 9 del README).
 * - Decoración (G882, encargo de diseño del 01-nov §1): paleta de los siete tipos, pared por puntos (dibujar, cerrar,
 *   terminar, mover, añadir y quitar puntos), giro (asa y campo, de 15 en 15 con «Ajustar a la rejilla»), texto en el sitio
 *   con el contador de 40, nombre opcional, «Elegir varias» (se mueven juntas), duplicar y quitar, y el límite de 2.000
 *   piezas por plano. Se guarda en el mismo borrador. Sin `sala.plano.editar`, se ve sin la paleta.
 * - «Revisar y publicar»: `previewFloorPlanImpact` con la fecha de efecto (próxima jornada sin servicio, otra fecha o
 *   «Ahora mismo» aunque haya servicio, G401), lo que cambia (con la decoración, que cuenta el panel comparando el
 *   borrador con la versión activa) y las reservas futuras que pierden su recurso; publicar con el `impact_token`
 *   (`publishFloorPlanDraft`; `409` = revisar otra vez). Si solo cambia la decoración, se publica igual.
 * - Versiones (`listFloorPlanVersions`): no se borran ni se cambian; «Volver a esta versión» abre un borrador con su dibujo.
 *
 * Propietario y Administrador editan (`sala.plano.editar`); Encargado y Recepción solo miran. Sin plano, «Crear el plano».
 */
const PERMISOS = { ver: 'sala.plano.ver', editar: 'sala.plano.editar' } as const;
export const ESPERA_GUARDADO_MS = 700;

const AYUDA: TarjetaAyuda[] = [
    { icono: 'pencil', titulo: 'Un borrador', texto: 'Dibujas sobre un único borrador. Lo que ves en Sala no cambia hasta que publicas.' },
    { icono: 'calendar-days', titulo: 'Fecha de efecto', texto: 'Al publicar eliges cuándo entra en vigor: la próxima jornada sin servicio, otro día o ahora mismo.' },
    { icono: 'history', titulo: 'Versiones', texto: 'Las versiones no se borran. Para volver a una, se abre un borrador con su dibujo y se publica otra vez.' },
];

const ESTADO_VERSION: Readonly<Record<ResumenVersion['status'], readonly [string, EstadoChip]>> = {
    draft: ['Borrador', 'draft'],
    scheduled: ['Programada', 'pending'],
    active: ['Activa', 'confirmed'],
    superseded: ['Sustituida', 'neutral'],
    withdrawn: ['Retirada', 'problem'],
};
const FORMAS: readonly { valor: Posicion['shape']; texto: string }[] = [
    { valor: 'round', texto: 'Redonda' },
    { valor: 'square', texto: 'Cuadrada' },
    { valor: 'rectangle', texto: 'Rectangular' },
    { valor: 'custom', texto: 'Otra' },
];

const ETag412 = (e: unknown) => e instanceof ErrorApi && e.estado === 412;

export function Plano(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const contexto = useContextoReservas(ops, negocio, membresia, nombreNegocio);
    const ver = permisos.puede(PERMISOS.ver);
    const edita = permisos.puede(PERMISOS.editar) && !soloLectura;
    const [vista, setVista] = useState(0);
    const [planes, setPlanes] = useState<PlanoApi[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [publicar, setPublicar] = useState(false);
    const [editar, setEditar] = useState(0);
    // «Editar el plano» desde Versiones: al volver a Dibujo, el editor se abre al montar.
    const [entrarEditando, setEntrarEditando] = useState(false);
    const recargar = useCallback(() => { setIntento((i) => i + 1); }, []);
    const avisar = useAvisoBreve();
    const [creando, setCreando] = useState(false);
    // El borrador que tiene abierto el Dibujo (con la versión de su último guardado, para el `If-Match` de publicar).
    const [borradorVivo, setBorradorVivo] = useState<ResumenVersion | null>(null);

    useEffect(() => {
        if (!ver) {
            return;
        }
        let vivo = true;
        ops.listFloorPlans(negocio, { limit: 50 })
            .then((l) => { if (vivo) { setPlanes(l.data.filter((p) => p.status === 'active')); setError(null); } })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => { vivo = false; };
    }, [ops, negocio, ver, intento]);

    const plan = planes?.[0] ?? null;
    const borradorActual = borradorVivo?.plan_id === plan?.id ? borradorVivo : plan?.draft ?? null;
    const hayBorrador = borradorActual !== null;
    useCabecera({
        ayuda: AYUDA,
        acciones: !edita || plan === null
            ? []
            : vista === 1
              ? [{ clave: 'editar', texto: 'Editar el plano', icono: 'pencil', onClick: () => { setVista(0); setEntrarEditando(true); setEditar((n) => n + 1); } }]
              : [
                  ...(hayBorrador ? [{ clave: 'publicar', texto: 'Revisar y publicar', icono: 'send' as const, onClick: () => { setPublicar(true); } }] : []),
                  { clave: 'editar', texto: 'Abrir el editor', icono: 'maximize' as const, onClick: () => { setEditar((n) => n + 1); } },
              ],
    });

    if (!ver) {
        return <EstadoSinPermiso seccion="Plano" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && planes === null) {
        const e = estadoDeError(error);
        return e.tipo === 'sin-permiso'
            ? <EstadoSinPermiso seccion="Plano" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />
            : <EstadoError seccion="Plano" codigoSoporte={e.codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (planes === null) {
        return <EstadoCargando seccion="Plano" />;
    }
    if (plan === null) {
        return (
            <div className="rv-rs-pantalla">
                <div className="rv-pl-vacio">
                    <span aria-hidden="true"><Icono nombre="map" /></span>
                    <strong>Aún no tienes plano</strong>
                    <small>Dibuja tus salas y coloca tus recursos. Los recursos ya existen en Recursos; aquí solo se colocan.</small>
                    {edita && (
                        <button
                            type="button"
                            className="rv-b rv-b-pri"
                            disabled={creando}
                            onClick={() => {
                                setCreando(true);
                                ops.createFloorPlan(negocio, { name: 'Plano' })
                                    .then(() => { recargar(); })
                                    .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido crear el plano.' }); })
                                    .finally(() => { setCreando(false); });
                            }}
                        >
                            <Icono nombre="plus" />
                            <span>Crear el plano</span>
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="rv-rs-pantalla">
            <div className="rv-en-barra">
                <Segmentos etiqueta="Apartados del plano" movil={movil} items={[{ etiqueta: 'Dibujo', icono: 'pencil' }, { etiqueta: 'Versiones', icono: 'history' }]} valor={vista} onCambio={setVista} />
            </div>
            {soloLectura && <p className="rv-sl-nota">El negocio está en solo lectura: puedes ver el plano, pero no cambiarlo.</p>}
            {!edita && !soloLectura && <p className="rv-sl-nota">Solo mirar: ves el plano con su decoración. Editarlo lo hace el propietario o un administrador.</p>}
            {vista === 0 ? (
                <Dibujo key={`${plan.id}${String(intento)}`} ops={ops} negocio={negocio} plan={plan} contexto={contexto} edita={edita} abrir={editar} abrirAlMontar={entrarEditando} onAbierto={() => { setEntrarEditando(false); }} onBorrador={setBorradorVivo} onCambio={() => { setBorradorVivo(null); recargar(); }} />
            ) : (
                <Versiones ops={ops} negocio={negocio} movil={movil} plan={plan} contexto={contexto} edita={edita} onCambio={() => { recargar(); setVista(0); }} />
            )}
            {publicar && borradorActual !== null && (
                <Publicar
                    ops={ops}
                    negocio={negocio}
                    movil={movil}
                    plan={plan}
                    borrador={borradorActual}
                    contexto={contexto}
                    onCerrar={(hecho) => {
                        setPublicar(false);
                        if (hecho !== null) {
                            setBorradorVivo(null);
                            avisar({ tipo: 'ok', texto: hecho.status === 'active' ? `Versión ${String(hecho.number)} publicada: ya está en vigor.` : `Versión ${String(hecho.number)} publicada: entra en vigor el ${diaLargo(hecho.effective_local_date ?? '')}.` });
                            recargar();
                        }
                    }}
                />
            )}
        </div>
    );
}

function Dibujo({ ops, negocio, plan, contexto, edita, abrir, abrirAlMontar, onAbierto, onBorrador, onCambio }: { ops: Operaciones; negocio: string; plan: PlanoApi; contexto: ContextoReservas; edita: boolean; abrir: number; abrirAlMontar: boolean; onAbierto: () => void; onBorrador: (v: ResumenVersion) => void; onCambio: () => void }) {
    const avisar = useAvisoBreve();
    const [activa, setActiva] = useState<VersionPlano | null | undefined>(undefined);
    const [borrador, setBorrador] = useState<VersionPlano | null | undefined>(undefined);
    const [contenido, setContenido] = useState<ContenidoPlano | null>(null);
    const [guardado, setGuardado] = useState<string | null>(null);
    const [superada, setSuperada] = useState(false);
    const [sala, setSala] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [elegido, setElegido] = useState<string | null>(null);
    const [descartar, setDescartar] = useState(false);
    const [retirar, setRetirar] = useState<string | null>(null);
    const [nuevaSala, setNuevaSala] = useState<string | null>(null);
    const [ocupado, setOcupado] = useState(false);
    // Decoración (G882): «Elegir varias», pared a medio dibujar, punto elegido, texto en el sitio, nombre que no cabe.
    const [varias, setVarias] = useState<string[] | null>(null);
    const [pared, setPared] = useState<Punto[] | null>(null);
    const [puntoElegido, setPuntoElegido] = useState<number | null>(null);
    const [texto, setTexto] = useState<{ indice: number; valor: string } | null>(null);
    const [nombreLocal, setNombreLocal] = useState<string | null>(null);
    const [conRejilla, setConRejilla] = useState(true);
    const [paleta, setPaleta] = useState(true);
    // Uniones predefinidas (G1200): sus mesas van pegadas, sin pasillo. Sin ellas (sin permiso o sin respuesta), ninguna.
    const [uniones, setUniones] = useState<string[][]>([]);
    const antesDelArrastre = useRef<ContenidoPlano | null>(null);
    const cambios = useRef(0);
    const reloj = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const version = useRef<VersionPlano | null>(null);
    const editando = contenido !== null;

    useEffect(() => {
        let vivo = true;
        const id = plan.effective_version?.id;
        if (id === undefined) {
            Promise.resolve().then(() => { if (vivo) { setActiva(null); } }).catch(() => undefined);
        } else {
            ops.getFloorPlanVersion(negocio, id).then((v) => { if (vivo) { setActiva(v); } }).catch(() => { if (vivo) { setActiva(null); } });
        }
        if (plan.draft == null) {
            Promise.resolve().then(() => { if (vivo) { setBorrador(null); } }).catch(() => undefined);
        } else {
            ops.getFloorPlanDraft(negocio, plan.id).then((v) => { if (vivo) { setBorrador(v); version.current = v; } }).catch(() => { if (vivo) { setBorrador(null); } });
        }
        return () => { vivo = false; };
    }, [ops, negocio, plan]);

    useEffect(() => () => { clearTimeout(reloj.current); }, []);

    useEffect(() => {
        if (!editando) {
            return;
        }
        let vivo = true;
        todas((cursor) => ops.listResourceUnions(negocio, { cursor, limit: 100 }))
            .then((l) => { if (vivo) { setUniones(l.filter((u) => u.status === 'active').map((u) => u.resource_ids ?? [])); } })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [editando, ops, negocio]);

    // «Abrir el editor» (la cabecera sube `abrir`): con borrador, se edita; sin él, se abre uno de la versión activa.
    const abierto = useRef(abrirAlMontar ? abrir - 1 : abrir);
    useEffect(() => {
        if (abrir <= abierto.current || !edita || borrador === undefined) {
            return;
        }
        abierto.current = abrir;
        const pedir = borrador === null ? ops.openFloorPlanDraft(negocio, plan.id) : Promise.resolve(borrador);
        pedir
            .then((v) => { version.current = v; setBorrador(v); setContenido(v.content); onBorrador(v); onAbierto(); })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido abrir el borrador.' }); });
    }, [abrir, edita, borrador, ops, negocio, plan.id, avisar, onAbierto, onBorrador]);

    const guardar = useCallback((c: ContenidoPlano) => {
        const v = version.current;
        if (v === null) {
            return;
        }
        const n = cambios.current;
        ops.saveFloorPlanDraft(negocio, plan.id, v, c)
            .then((nuevo) => {
                version.current = nuevo;
                onBorrador(nuevo);
                if (cambios.current === n) {
                    setGuardado(new Date().toISOString());
                }
            })
            .catch((e: unknown) => {
                if (ETag412(e)) {
                    setSuperada(true);
                } else {
                    avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido guardar el borrador.' });
                }
            });
    }, [ops, negocio, plan.id, avisar, onBorrador]);

    const cambiar = (c: ContenidoPlano) => {
        setContenido(c);
        cambios.current += 1;
        clearTimeout(reloj.current);
        reloj.current = setTimeout(() => { guardar(c); }, ESPERA_GUARDADO_MS);
    };

    const fuente = contenido ?? borrador?.content ?? activa?.content ?? null;
    if (activa === undefined || borrador === undefined) {
        return <EstadoCargando seccion="Plano" />;
    }
    const salas = [...(fuente?.rooms ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const salaActual = salas[Math.min(sala, Math.max(0, salas.length - 1))];
    const ref = salaActual?.room_id ?? salaActual?.name ?? '';
    const enSala = (r: string) => r === ref || r === salaActual?.name;
    const posiciones = (fuente?.positions ?? []).filter((p) => enSala(p.room_ref));
    const colocados = new Set((fuente?.positions ?? []).map((p) => p.resource_id));
    const sinColocar = contexto.recursos.filter((r) => !colocados.has(r.id));
    const elegida = posiciones.find((p) => p.resource_id === elegido) ?? null;
    const recursoElegido = contexto.recursos.find((r) => r.id === elegido);
    const decos = fuente?.decorations ?? [];
    const piezas = decos.map((pieza, indice) => ({ indice, pieza })).filter((p) => enSala(p.pieza.room_ref));
    const indiceElegido = elegido === null ? null : indiceDeClave(elegido);
    const piezaElegida = indiceElegido === null ? null : decos[indiceElegido] ?? null;
    const lleno = decos.length >= MAX_PIEZAS;
    const elegidas = varias ?? [];
    const decoElegidas = elegidas.map(indiceDeClave).filter((i): i is number => i !== null);

    const limpiarDeco = () => { setPuntoElegido(null); setTexto(null); setNombreLocal(null); };
    const elegir = (clave: string | null) => {
        if (varias !== null) {
            if (clave !== null) {
                setVarias(varias.includes(clave) ? varias.filter((k) => k !== clave) : [...varias, clave]);
            }
            return;
        }
        if (clave !== elegido) {
            limpiarDeco();
        }
        setElegido(clave);
    };
    const avisoLimite = () => { avisar({ tipo: 'error', texto: AVISO_LIMITE }); };
    const cambiarDecos = (f: (l: Decoracion[]) => Decoracion[]) => {
        if (contenido === null) {
            return;
        }
        cambiar({ ...contenido, decorations: f([...(contenido.decorations ?? [])]) });
    };
    const cambiarPieza = (i: number, f: (d: Decoracion) => Decoracion) => { cambiarDecos((l) => l.map((d, k) => (k === i ? f(d) : d))); };
    const ponerGeometria = (i: number, g: Partial<Decoracion['geometry']>) => { cambiarPieza(i, (d) => ({ ...d, geometry: { ...d.geometry, ...g } })); };
    const ponerPuntos = (i: number, abiertos: readonly Punto[], cerrar: boolean) => { ponerGeometria(i, { points: conCierre(abiertos, cerrar) }); };
    const anadirPieza = (tipo: TipoPieza) => {
        if (contenido === null || salaActual === undefined) {
            return;
        }
        if (lleno) {
            avisoLimite();
            return;
        }
        setVarias(null);
        limpiarDeco();
        if (tipo === 'wall') {
            setElegido(null);
            setPared([]);
            return;
        }
        const indice = (contenido.decorations ?? []).length;
        // G1200: en el centro o, si ahí pisa algo, en el hueco libre más cercano; si no cabe, no entra.
        const nuevo = anadirSinPisar({ ...contenido, decorations: [...(contenido.decorations ?? []), piezaNueva(tipo, ref, { x: salaActual.width / 2, y: salaActual.height / 2 })] }, claveDeco(indice));
        if (nuevo === null) {
            return;
        }
        cambiar(nuevo);
        setElegido(claveDeco(indice));
        if (tipo === 'text') {
            setTexto({ indice, valor: 'Texto' });
        }
    };
    const terminarPared = (cerrar: boolean) => {
        if (pared === null || pared.length < (cerrar ? 3 : 2) || contenido === null) {
            return;
        }
        if (cerrar && !sePuedeCerrar(pared.length)) {
            avisar({ tipo: 'error', texto: AVISO_CIERRE_LLENO });
            return;
        }
        if (lleno) {
            avisoLimite();
            return;
        }
        const indice = (contenido.decorations ?? []).length;
        // G1200: una pared que pisaría una mesa no se hace (se puede seguir dibujándola).
        const nuevo = anadirSinPisar({ ...contenido, decorations: [...(contenido.decorations ?? []), paredNueva(ref, pared, cerrar)] }, claveDeco(indice));
        if (nuevo === null) {
            return;
        }
        cambiar(nuevo);
        setPared(null);
        setElegido(claveDeco(indice));
    };
    const duplicar = (indices: readonly number[]) => {
        if (indices.length === 0 || contenido === null) {
            return;
        }
        const lista = contenido.decorations ?? [];
        if (lista.length + indices.length > MAX_PIEZAS) {
            avisoLimite();
            return;
        }
        const copias = indices.map((i) => lista[i]).filter((d): d is Decoracion => d !== undefined).map(duplicarPieza);
        const nuevas = copias.map((_, k) => claveDeco(lista.length + k));
        // G1208: cada copia pasa la regla de la huella (G1200): si pisa algo, al hueco libre más cercano; si alguna no cabe,
        // no se duplica nada y se dice por qué.
        let c: ContenidoPlano = contenido;
        let recolocada: string | null = null;
        for (const [k, copia] of copias.entries()) {
            const conCopia: ContenidoPlano = { ...c, decorations: [...(c.decorations ?? []), copia] };
            const e = escenaDe(conCopia);
            const r = e === null ? { contenido: conCopia } : asentar(e, conCopia, claveDeco(lista.length + k));
            if ('aviso' in r) {
                avisar({ tipo: 'error', texto: r.aviso });
                return;
            }
            if (r.contenido !== conCopia && recolocada === null) {
                recolocada = `${nombreDe(conCopia, claveDeco(lista.length + k))} pisaba otra pieza: la hemos dejado en el hueco libre más cercano.`;
            }
            c = r.contenido;
        }
        cambiar(c);
        if (recolocada !== null) {
            avisar({ tipo: 'ok', texto: recolocada });
        }
        limpiarDeco();
        if (varias === null) {
            setElegido(nuevas[0] ?? null);
        } else {
            setVarias(nuevas);
        }
    };
    const quitarVarias = () => {
        if (contenido === null || varias === null) {
            return;
        }
        const recursos = varias.filter((k) => indiceDeClave(k) === null);
        const fuera = new Set(decoElegidas);
        cambiar({ ...contenido, positions: contenido.positions.filter((p) => !recursos.includes(p.resource_id)), decorations: (contenido.decorations ?? []).filter((_, i) => !fuera.has(i)) });
        const partes = [
            recursos.length > 0 ? `${String(recursos.length)} ${recursos.length === 1 ? contexto.palabra : `${contexto.palabra}s`} fuera del plano (siguen en Recursos)` : '',
            fuera.size > 0 ? `${String(fuera.size)} ${fuera.size === 1 ? 'pieza de decoración quitada' : 'piezas de decoración quitadas'}` : '',
        ].filter((x) => x !== '');
        avisar({ tipo: 'ok', texto: `${mayuscula(partes.join(' y '))}.` });
        setVarias([]);
    };
    const arrastrar = (claves: readonly string[], dx: number, dy: number) => {
        const b = antesDelArrastre.current ?? contenido;
        if (b === null) {
            return;
        }
        const fuera = new Set(claves);
        const dentro = (v: number) => Math.min(10000, Math.max(0, v));
        cambiar({
            ...b,
            positions: b.positions.map((p) => (fuera.has(p.resource_id) ? { ...p, x: dentro(p.x + dx), y: dentro(p.y + dy) } : p)),
            decorations: (b.decorations ?? []).map((d, i) => (fuera.has(claveDeco(i)) ? moverPieza(d, dx, dy) : d)),
        });
    };
    const quitarPunto = () => {
        if (indiceElegido === null || piezaElegida === null || puntoElegido === null) {
            return;
        }
        const pts = puntosDe(piezaElegida) ?? [];
        const abiertos = puntosAbiertos(pts);
        if (abiertos.length <= 2) {
            avisar({ tipo: 'error', texto: 'Una pared necesita al menos dos puntos. Para quitarla entera, pulsa «Quitar la pared».' });
            return;
        }
        const quedan = abiertos.filter((_, k) => k !== puntoElegido);
        ponerPuntos(indiceElegido, quedan, estaCerrada(pts) && quedan.length >= 3);
        setPuntoElegido(null);
    };
    const cerrarOAbrir = () => {
        if (indiceElegido === null || piezaElegida === null) {
            return;
        }
        const pts = puntosDe(piezaElegida) ?? [];
        const abiertos = puntosAbiertos(pts);
        if (estaCerrada(pts)) {
            ponerPuntos(indiceElegido, abiertos, false);
        } else if (abiertos.length < 3) {
            avisar({ tipo: 'error', texto: 'Para cerrar una pared hacen falta tres puntos o más.' });
        } else if (!sePuedeCerrar(abiertos.length)) {
            avisar({ tipo: 'error', texto: AVISO_CIERRE_LLENO });
        } else {
            ponerPuntos(indiceElegido, abiertos, true);
        }
    };
    const edicionDeco = {
        conRejilla,
        pared,
        onPuntoPared: (p: Punto) => {
            if (pared === null) {
                return;
            }
            const ultimo = pared[pared.length - 1];
            if (ultimo !== undefined && ultimo[0] === p[0] && ultimo[1] === p[1]) {
                return;
            }
            if (!cabeOtroPunto(pared.length, false)) {
                avisar({ tipo: 'error', texto: AVISO_PUNTOS });
                return;
            }
            if (pared.length === MAX_PUNTOS - 2) {
                avisar({ tipo: 'error', texto: AVISO_CIERRALA_YA }); // C.71: el siguiente punto ya no deja cerrarla.
            }
            setPared([...pared, p]);
        },
        onCerrarPared: () => { terminarPared(true); },
        puntoElegido,
        onElegirPunto: setPuntoElegido,
        onMoverPunto: (indice: number, i: number, p: Punto) => {
            const d = decos[indice];
            const pts = d === undefined ? null : puntosDe(d);
            if (pts === null) {
                return;
            }
            const abiertos = puntosAbiertos(pts);
            abiertos[i] = p;
            ponerPuntos(indice, abiertos, estaCerrada(pts));
            setPuntoElegido(i);
        },
        onAnadirPunto: (indice: number, tramo: number) => {
            const d = decos[indice];
            const pts = d === undefined ? null : puntosDe(d);
            if (pts === null) {
                return;
            }
            const cerrada = estaCerrada(pts);
            const abiertos = puntosAbiertos(pts);
            if (!cabeOtroPunto(abiertos.length, cerrada)) {
                avisar({ tipo: 'error', texto: AVISO_PUNTOS });
                return;
            }
            const a = abiertos[tramo] ?? [0, 0];
            const b = abiertos[(tramo + 1) % abiertos.length] ?? [0, 0];
            abiertos.splice(tramo + 1, 0, [ajustar((a[0] + b[0]) / 2, conRejilla ? PASO_REJILLA : 1), ajustar((a[1] + b[1]) / 2, conRejilla ? PASO_REJILLA : 1)]);
            ponerPuntos(indice, abiertos, cerrada);
            setPuntoElegido(tramo + 1);
        },
        onGirar: (indice: number, g: number) => { ponerGeometria(indice, { rotation: g }); },
        onTamano: (indice: number, ancho: number, alto: number) => { ponerGeometria(indice, { width: ancho, height: alto }); },
        texto,
        onEscribir: (indice: number) => {
            const d = decos[indice];
            if (d === undefined) {
                return;
            }
            setVarias(null);
            setElegido(claveDeco(indice));
            setNombreLocal(null);
            setTexto({ indice, valor: d.label ?? '' });
        },
        onTexto: (valor: string) => {
            if (texto === null) {
                return;
            }
            setTexto({ ...texto, valor });
            const d = decos[texto.indice];
            if (d !== undefined && errorDeNombre(valor, d.kind === 'text') === null) {
                cambiarPieza(texto.indice, (x) => conNombre(x, valor));
            }
        },
        onTextoHecho: () => {
            const d = texto === null ? undefined : decos[texto.indice];
            if (texto === null || d === undefined || errorDeNombre(texto.valor, d.kind === 'text') !== null) {
                return;
            }
            setTexto(null);
        },
    };
    const cambiarPosicion = (id: string, datos: Partial<Posicion>) => {
        if (contenido === null) {
            return;
        }
        cambiar({ ...contenido, positions: contenido.positions.map((p) => (p.resource_id === id ? { ...p, ...datos } : p)) });
    };
    const palabra = (r: Recurso | undefined) => `${mayuscula(contexto.zonas.find((z) => z.id === r?.current_terms.zone_id)?.unit ?? contexto.palabra)} ${r?.code ?? ''}`.trim();

    // ---- Regla de la huella (G1200, solo en el panel): nada montado ni pegado ----
    const escenaDe = (c: ContenidoPlano): Escena | null => (salaActual === undefined ? null : {
        sala: { ref, nombre: salaActual.name, ancho: salaActual.width, alto: salaActual.height },
        posiciones: c.positions.filter((p) => enSala(p.room_ref)),
        piezas: (c.decorations ?? []).map((pieza, indice) => ({ indice, pieza })).filter((p) => enSala(p.pieza.room_ref)),
        conSillas: (id) => (contexto.recursos.find((r) => r.id === id)?.kind ?? 'table') === 'table',
        uniones,
        nombre: (clave) => {
            const i = indiceDeClave(clave);
            const d = i === null ? undefined : c.decorations?.[i];
            return i === null ? palabra(contexto.recursos.find((r) => r.id === clave)) : d === undefined ? 'otra pieza' : NOMBRE_TIPO[d.kind].toLowerCase();
        },
    });
    /** Lo que pisa cada clave en `c`, sin contar las que se mueven con ella. */
    const choquesEn = (e: Escena, c: ContenidoPlano, clave: string, juntas: ReadonlySet<string>) => {
        const i = indiceDeClave(clave);
        if (i === null) {
            const p = c.positions.find((x) => x.resource_id === clave);
            return p === undefined ? [] : choquesDeRecurso(e, p, juntas);
        }
        const d = c.decorations?.[i];
        return d === undefined ? [] : choquesDePieza(e, i, d, juntas);
    };
    /** «La barra «Barra principal»» o «Mesa P3»: lo que se ha movido, para el aviso. */
    const nombreDe = (c: ContenidoPlano, clave: string) => {
        const i = indiceDeClave(clave);
        if (i === null) {
            return palabra(contexto.recursos.find((r) => r.id === clave));
        }
        const d = c.decorations?.[i];
        const articulo: Partial<Record<TipoPieza, string>> = { wall: 'La pared', bar: 'La barra', stage: 'El escenario', door: 'La puerta', plant: 'La planta' };
        const nombre = (d?.label ?? '').trim();
        return `${(d === undefined ? undefined : articulo[d.kind]) ?? 'La pieza'}${nombre !== '' && d?.kind !== 'text' && nombre !== NOMBRE_TIPO[d?.kind ?? 'other'] ? ` «${nombre}»` : ''}`;
    };
    /**
     * Asienta en el hueco libre más cercano la pieza `clave` de `c` si pisa algo; una pared no salta a otro sitio. Devuelve
     * el contenido asentado o, si no cabe, el aviso.
     */
    const asentar = (e: Escena, c: ContenidoPlano, clave: string): { contenido: ContenidoPlano } | { aviso: string } => {
        const choques = choquesEn(e, c, clave, new Set());
        if (choques.length === 0) {
            return { contenido: c };
        }
        const i = indiceDeClave(clave);
        const d = i === null ? undefined : c.decorations?.[i];
        if (d !== undefined && puntosDe(d) !== null) {
            return { aviso: `Ese tramo de pared ${queSePisa(e, choques)}: pon el punto un poco más lejos.` };
        }
        if (i === null) {
            const p = c.positions.find((x) => x.resource_id === clave);
            const hueco = p === undefined ? null : huecoParaRecurso(e, p);
            return hueco === null
                ? { aviso: `${nombreDe(c, clave)} no cabe en ${e.sala.nombre}: no queda sitio para sus sillas y un pasillo. Quita o achica alguna pieza, o agranda la sala.` }
                : { contenido: { ...c, positions: c.positions.map((x) => (x.resource_id === clave ? hueco : x)) } };
        }
        const hueco = d === undefined ? null : huecoParaPieza(e, i, d);
        return hueco === null
            ? { aviso: `${nombreDe(c, clave)} no cabe en ${e.sala.nombre}: no queda sitio sin pisar otra pieza. Quita o achica alguna pieza, o agranda la sala.` }
            : { contenido: { ...c, decorations: (c.decorations ?? []).map((x, k) => (k === i ? hueco : x)) } };
    };
    /**
     * La regla de la huella sobre un cambio ya hecho en `c` (G1200): mover una sola pieza la lleva al hueco libre más cercano;
     * lo demás (varias juntas, tamaño, giro, punto de pared) o lo que no cabe se rechaza con el porqué. Devuelve el contenido
     * que queda (`c` si no pisa nada) con el aviso de que se ha recolocado, o el aviso de por qué no se hace.
     */
    const resolverHuella = (e: Escena, c: ContenidoPlano, modo: Arrastre['modo'], claves: readonly string[]): { contenido: ContenidoPlano; recolocada: string | null } | { error: string } => {
        const juntas = new Set(claves);
        const pisa = claves.map((k) => choquesEn(e, c, k, juntas)).find((x) => x.length > 0);
        if (pisa === undefined) {
            return { contenido: c, recolocada: null };
        }
        const quien = queSePisa(e, pisa);
        const clave = claves[0] ?? '';
        if (modo === 'tamano') {
            return { error: indiceDeClave(clave) === null ? `${nombreDe(c, clave)} no cabe más grande ahí: sus sillas pisarían otra pieza.` : 'No cabe más grande ahí: pisaría otra pieza.' };
        }
        if (modo === 'giro') {
            return { error: `Girada así ${quien}: la dejamos como estaba.` };
        }
        if (modo === 'punto') {
            return { error: `Con ese punto, la pared ${quien}: lo dejamos donde estaba.` };
        }
        if (claves.length > 1) {
            return { error: `Así ${quien.replace(/^pisaría/, 'pisarían').replace(/^se saldría/, 'se saldrían')}: las dejamos donde estaban.` };
        }
        const r = asentar(e, c, clave);
        return 'aviso' in r ? { error: r.aviso } : { contenido: r.contenido, recolocada: `${nombreDe(c, clave)} pisaba otra pieza: la hemos dejado en el hueco libre más cercano.` };
    };
    /** Al soltar (G1200): lo que pisa salta al hueco libre más cercano; lo que no puede, vuelve a como estaba y se dice por qué. */
    const alSoltar = (modo: Arrastre['modo'], claves: readonly string[]) => {
        const antes = antesDelArrastre.current;
        const c = contenido;
        const e = c === null ? null : escenaDe(c);
        if (c === null || e === null || antes === null) {
            return;
        }
        const r = resolverHuella(e, c, modo, claves);
        if ('error' in r) {
            cambiar(antes);
            avisar({ tipo: 'error', texto: r.error });
        } else if (r.recolocada !== null) {
            cambiar(r.contenido);
            avisar({ tipo: 'ok', texto: r.recolocada });
        }
    };
    /**
     * Al confirmar un campo de medida (G1208: X, Y, Ancho, Fondo o Giro, al salir del campo o con Enter): el cambio pasa la
     * misma regla que al arrastrar (X e Y, como mover; Ancho y Fondo, como el asa; Giro, como girar). Si no cabe, no se hace:
     * el campo vuelve a su valor y se dice por qué.
     */
    const confirmarMedida = (clave: string, modo: 'grupo' | 'tamano' | 'giro', nuevo: ContenidoPlano) => {
        const e = escenaDe(nuevo);
        if (e === null) {
            cambiar(nuevo);
            return;
        }
        const r = resolverHuella(e, nuevo, modo, [clave]);
        if ('error' in r) {
            avisar({ tipo: 'error', texto: r.error });
            return;
        }
        cambiar(r.contenido);
        if (r.recolocada !== null) {
            avisar({ tipo: 'ok', texto: r.recolocada });
        }
    };
    const medidaDeRecurso = (id: string, datos: Partial<Posicion>, modo: 'grupo' | 'tamano' | 'giro') => {
        if (contenido !== null) {
            confirmarMedida(id, modo, { ...contenido, positions: contenido.positions.map((p) => (p.resource_id === id ? { ...p, ...datos } : p)) });
        }
    };
    const medidaDePieza = (i: number, g: Partial<Decoracion['geometry']>, modo: 'grupo' | 'tamano' | 'giro') => {
        if (contenido !== null) {
            confirmarMedida(claveDeco(i), modo, { ...contenido, decorations: (contenido.decorations ?? []).map((d, k) => (k === i ? { ...d, geometry: { ...d.geometry, ...g } } : d)) });
        }
    };
    /** Una pieza o un recurso nuevo (en el centro de la sala): si ahí pisa algo, al hueco libre más cercano; si no cabe, no entra. */
    const anadirSinPisar = (c: ContenidoPlano, clave: string): ContenidoPlano | null => {
        const e = escenaDe(c);
        if (e === null) {
            return c;
        }
        const r = asentar(e, c, clave);
        if ('aviso' in r) {
            avisar({ tipo: 'error', texto: r.aviso });
            return null;
        }
        if (r.contenido !== c) {
            avisar({ tipo: 'ok', texto: `${nombreDe(c, clave)} va en el hueco libre más cercano: en el centro pisaba otra pieza.` });
        }
        return r.contenido;
    };

    return (
        <>
            <div className="rv-pl-estado">
                <span>
                    {plan.effective_version == null ? <strong>Sin versión publicada</strong> : <><strong>{`Versión ${String(plan.effective_version.number)}`}</strong>{plan.effective_version.effective_local_date != null && <small>{` En vigor desde el ${diaCorto(plan.effective_version.effective_local_date)}`}</small>}</>}
                </span>
                {plan.scheduled_version != null && (
                    <span>
                        <Chip estado="pending">Programada</Chip>
                        <strong>{`Versión ${String(plan.scheduled_version.number)}`}</strong>
                        {plan.scheduled_version.effective_local_date != null && <small>{` entra el ${diaCorto(plan.scheduled_version.effective_local_date)}`}</small>}
                        {edita && <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={() => { setRetirar(''); }}><Icono nombre="circle-x" /><span>Retirar</span></button>}
                    </span>
                )}
                {borrador !== null && (
                    <span>
                        <Chip estado="draft">Borrador</Chip>
                        <strong>{plan.effective_version == null ? 'Primera versión' : `Sobre la versión ${String(plan.effective_version.number)}`}</strong>
                        {guardado !== null && <small>{` Guardado a las ${horaEn(guardado, contexto.zona)}`}</small>}
                        {edita && <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={() => { setDescartar(true); }}><Icono nombre="trash-2" /><span>Descartar el borrador</span></button>}
                    </span>
                )}
            </div>
            {superada && (
                <div className="rv-rc-aviso" role="alert">
                    <span className="rv-rc-aviso__icono" aria-hidden="true"><Icono nombre="triangle-alert" /></span>
                    <span><strong>Versión superada</strong><small>Otra persona ha guardado el borrador después de que lo abrieras. Carga el último para no pisar sus cambios.</small></span>
                    <button type="button" className="rv-b rv-b-sec" onClick={onCambio}><Icono nombre="refresh-cw" /><span>Cargar el último</span></button>
                </div>
            )}
            <div className="rv-pl-barra">
                {salas.length > 0 && (
                    <div className="rv-sl-filtro">
                        <button type="button" className="rv-sl-filtro__pastilla" aria-label={`Sala: ${salaActual?.name ?? ''}`}><span>{salaActual?.name}</span></button>
                        {salas.length > 1 && <button type="button" className="rv-sl-redondo rv-sl-redondo--chico" aria-label="Ver la sala siguiente" onClick={() => { setSala((s) => (s + 1) % salas.length); setElegido(null); setVarias(null); setPared(null); limpiarDeco(); }}><Icono nombre="chevron-right" /></button>}
                    </div>
                )}
                {editando && <button type="button" className="rv-b rv-b-sec" onClick={() => { setNuevaSala(''); }}><Icono nombre="plus" /><span>Añadir una sala</span></button>}
                {editando && salaActual !== undefined && (
                    <button
                        type="button"
                        className="rv-b rv-b-sec"
                        aria-pressed={varias !== null}
                        onClick={() => { setPared(null); limpiarDeco(); setElegido(null); setVarias(varias === null ? [] : null); }}
                    >
                        <Icono nombre="list-checks" /><span>{varias === null ? 'Elegir varias' : 'Terminar de elegir'}</span>
                    </button>
                )}
                <span className="rv-sl-zoom">
                    <button type="button" aria-label="Alejar" onClick={() => { setZoom((z) => Math.max(0.6, z - 0.2)); }}><Icono nombre="zoom-out" /></button>
                    <span>{`${String(Math.round(zoom * 100))} %`}</span>
                    <button type="button" aria-label="Acercar" onClick={() => { setZoom((z) => Math.min(2, z + 0.2)); }}><Icono nombre="zoom-in" /></button>
                </span>
            </div>
            {editando && pared !== null && (
                <div className="rv-pl-guia" role="status">
                    <p>
                        <Icono nombre="minus" />
                        {guiaPared(pared.length)}
                    </p>
                    <span>
                        <button type="button" className="rv-b rv-b-sec" disabled={pared.length === 0} onClick={() => { setPared(pared.slice(0, -1)); }}><Icono nombre="undo-2" /><span>Quitar el punto</span></button>
                        {pared.length >= 3 && <button type="button" className="rv-b rv-b-sec" disabled={!sePuedeCerrar(pared.length)} onClick={() => { terminarPared(true); }}><Icono nombre="square" /><span>Cerrar</span></button>}
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { setPared(null); }}><Icono nombre="x" /><span>Cancelar</span></button>
                        <button type="button" className="rv-b rv-b-pri" disabled={pared.length < 2} onClick={() => { terminarPared(false); }}><Icono nombre="check" /><span>Terminar</span></button>
                    </span>
                </div>
            )}
            {editando && varias !== null && (
                <div className="rv-pl-guia" role="status">
                    <p>
                        <Icono nombre="list-checks" />
                        {elegidas.length > 1
                            ? `${String(elegidas.length)} elegidas. Arrastra una para moverlas juntas; toca otra para añadirla o quitarla.`
                            : elegidas.length === 1 ? `1 elegida. Toca más ${contexto.palabra}s o piezas de decoración.` : `Toca los ${contexto.palabra}s y las piezas de decoración que quieras elegir.`}
                    </p>
                </div>
            )}
            <div className="rv-pl-cuerpo">
                <div className="rv-pl-hoja">
                    {salaActual === undefined ? (
                        <p className="rv-sl-nota rv-sl-nota--centro">{editando ? 'Aún no hay salas. Añade la primera.' : 'El plano aún no tiene salas.'}</p>
                    ) : (
                        <Lienzo
                            nombre={salaActual.name}
                            ancho={salaActual.width}
                            alto={salaActual.height}
                            posiciones={posiciones}
                            piezas={piezas}
                            recursos={contexto.recursos}
                            zoom={zoom}
                            editando={editando}
                            elegido={varias === null ? elegido : null}
                            elegidas={new Set(elegidas)}
                            onElegir={elegir}
                            onEmpezarArrastre={() => { antesDelArrastre.current = contenido; }}
                            onArrastrar={arrastrar}
                            onTamanoRecurso={(id, ancho, alto) => { cambiarPosicion(id, { width: ancho, height: alto }); }}
                            onSoltar={alSoltar}
                            decoracion={edicionDeco}
                        />
                    )}
                </div>
                {editando && varias !== null ? (
                    <aside className="rv-pl-lado" aria-label="Varias elegidas">
                        <header className="rv-pl-lado__cab">
                            <span><small>Elegir varias</small><strong>{elegidas.length === 1 ? '1 elegida' : `${String(elegidas.length)} elegidas`}</strong><small>Arrastra una para moverlas juntas.</small></span>
                            <button type="button" className="rv-b rv-b-sec" onClick={() => { setVarias(null); }}><Icono nombre="check" /><span>Terminar</span></button>
                        </header>
                        {decoElegidas.length > 0 && (
                            <button type="button" className="rv-b rv-b-sec" onClick={() => { duplicar(decoElegidas); }}>
                                <Icono nombre="copy" /><span>{decoElegidas.length === 1 ? 'Duplicar la pieza de decoración' : `Duplicar las ${String(decoElegidas.length)} piezas de decoración`}</span>
                            </button>
                        )}
                        {elegidas.length > 0 && <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={quitarVarias}><Icono nombre="trash-2" /><span>Quitar</span></button>}
                    </aside>
                ) : editando && piezaElegida !== null && indiceElegido !== null ? (
                    <PiezaElegida
                        pieza={piezaElegida}
                        sala={salaActual?.name ?? ''}
                        conRejilla={conRejilla}
                        nombreLocal={nombreLocal}
                        puntoElegido={puntoElegido}
                        onHecho={() => { setElegido(null); limpiarDeco(); }}
                        onTipo={(t) => { cambiarPieza(indiceElegido, (d) => cambiarTipo(d, t)); setPuntoElegido(null); setNombreLocal(null); }}
                        onNombre={(v) => {
                            if (errorDeNombre(v, piezaElegida.kind === 'text') === null) {
                                setNombreLocal(null);
                                cambiarPieza(indiceElegido, (d) => conNombre(d, v));
                            } else {
                                setNombreLocal(v);
                            }
                        }}
                        onMedida={(g, modo) => { medidaDePieza(indiceElegido, g, modo); }}
                        onEscribir={() => { edicionDeco.onEscribir(indiceElegido); }}
                        onQuitarPunto={quitarPunto}
                        onCerrarOAbrir={cerrarOAbrir}
                        onDuplicar={() => { duplicar([indiceElegido]); }}
                        onQuitar={() => { cambiarDecos((l) => l.filter((_, k) => k !== indiceElegido)); setElegido(null); limpiarDeco(); }}
                    />
                ) : (elegida !== null || (editando && salaActual !== undefined)) && (
                    <aside className="rv-pl-lado" aria-label={elegida === null ? 'Sin colocar' : palabra(recursoElegido)}>
                        {elegida === null ? (
                            <>
                                <h3 className="rv-rs-subtitulo">{`Sin colocar (${String(sinColocar.length)})`}</h3>
                                {sinColocar.length === 0 ? <p className="rv-sl-nota">Todos tus recursos están en el plano. Los nuevos se crean en Recursos.</p> : (
                                    <ul className="rv-pl-sin">
                                        {sinColocar.map((r) => (
                                            <li key={r.id}>
                                                <span>{palabra(r)}<small>{` ${String(r.current_terms.min_capacity)} a ${String(r.current_terms.max_capacity)}`}</small></span>
                                                <button
                                                    type="button"
                                                    className="rv-b rv-b-sec"
                                                    aria-label={`Colocar ${palabra(r)}`}
                                                    onClick={() => {
                                                        if (contenido === null) {
                                                            return;
                                                        }
                                                        // G1200: entra en el centro de la sala o, si ahí pisa algo, en el hueco libre más cercano.
                                                        const centro = (total: number) => Math.max(0, ajustar((total - 90) / 2, PASO_REJILLA));
                                                        const nuevo = anadirSinPisar({ ...contenido, positions: [...contenido.positions, { resource_id: r.id, room_ref: ref, shape: 'square', x: centro(salaActual?.width ?? 0), y: centro(salaActual?.height ?? 0), width: 90, height: 90, rotation: 0 }] }, r.id);
                                                        if (nuevo === null) {
                                                            return;
                                                        }
                                                        cambiar(nuevo);
                                                        setElegido(r.id);
                                                    }}
                                                >
                                                    <Icono nombre="plus" /><span>Colocar</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <Paleta
                                    cantidad={decos.length}
                                    abierta={paleta}
                                    dibujandoPared={pared !== null}
                                    conRejilla={conRejilla}
                                    onAbrir={() => { setPaleta((a) => !a); }}
                                    onAnadir={anadirPieza}
                                    onRejilla={setConRejilla}
                                />
                            </>
                        ) : (
                            <>
                                <header className="rv-pl-lado__cab">
                                    <span><small>{`${mayuscula(contexto.palabra)} • ${salaActual?.name ?? ''}`}</small><strong>{palabra(recursoElegido)}</strong><small>{recursoElegido === undefined ? '' : `${String(recursoElegido.current_terms.min_capacity)} a ${String(recursoElegido.current_terms.max_capacity)} personas`}</small></span>
                                    <button type="button" className="rv-b rv-b-sec" onClick={() => { setElegido(null); }}><Icono nombre="check" /><span>Hecho</span></button>
                                </header>
                                {editando ? (
                                    <div className="rv-sl-form">
                                        <Desplegable etiqueta="Forma" valor={elegida.shape} opciones={FORMAS} onChange={(v) => { cambiarPosicion(elegida.resource_id, { shape: v as Posicion['shape'] }); }} />
                                        <div className="rv-rc-dos rv-pl-par">
                                            <CampoMedida etiqueta="Posición X" valor={elegida.x} min={0} max={10000} onConfirmar={(v) => { medidaDeRecurso(elegida.resource_id, { x: v }, 'grupo'); }} />
                                            <CampoMedida etiqueta="Posición Y" valor={elegida.y} min={0} max={10000} onConfirmar={(v) => { medidaDeRecurso(elegida.resource_id, { y: v }, 'grupo'); }} />
                                        </div>
                                        <div className="rv-rc-dos rv-pl-par">
                                            <CampoMedida etiqueta="Ancho" valor={elegida.width} min={1} max={10000} onConfirmar={(v) => { medidaDeRecurso(elegida.resource_id, { width: v }, 'tamano'); }} />
                                            <CampoMedida etiqueta="Fondo" valor={elegida.height} min={1} max={10000} onConfirmar={(v) => { medidaDeRecurso(elegida.resource_id, { height: v }, 'tamano'); }} />
                                        </div>
                                        <CampoMedida etiqueta="Giro (grados)" valor={elegida.rotation ?? 0} min={0} max={359} onConfirmar={(v) => { medidaDeRecurso(elegida.resource_id, { rotation: v }, 'giro'); }} />
                                        <dl className="rv-rs-presupuesto">
                                            <div><dt>Zona</dt><dd>{contexto.zonas.find((z) => z.id === recursoElegido?.current_terms.zone_id)?.name ?? '—'}</dd></div>
                                        </dl>
                                        <p className="rv-sl-nota">La zona y la capacidad se cambian en Recursos.</p>
                                        <button
                                            type="button"
                                            className="rv-b rv-b-sec rv-b-rojo"
                                            onClick={() => {
                                                cambiar({ ...contenido, positions: contenido.positions.filter((p) => p.resource_id !== elegida.resource_id) });
                                                setElegido(null);
                                            }}
                                        >
                                            <Icono nombre="minus" /><span>Quitar del plano</span>
                                        </button>
                                    </div>
                                ) : (
                                    <dl className="rv-rs-presupuesto">
                                        <div><dt>Forma</dt><dd>{FORMAS.find((f) => f.valor === elegida.shape)?.texto ?? ''}</dd></div>
                                        <div><dt>Posición</dt><dd>{`${String(elegida.x)}, ${String(elegida.y)}`}</dd></div>
                                        <div><dt>Tamaño</dt><dd>{`${String(elegida.width)} × ${String(elegida.height)}`}</dd></div>
                                        <div><dt>Giro</dt><dd>{`${String(elegida.rotation ?? 0)}°`}</dd></div>
                                        <div><dt>Zona</dt><dd>{contexto.zonas.find((z) => z.id === recursoElegido?.current_terms.zone_id)?.name ?? '—'}</dd></div>
                                    </dl>
                                )}
                            </>
                        )}
                    </aside>
                )}
            </div>
            <Ventana
                abierta={descartar}
                titulo="¿Descartar el borrador?"
                texto="Se pierden los cambios que no has publicado. El plano en vigor no cambia."
                accion="Descartar"
                accionIcono="trash-2"
                cancelar="Volver"
                ocupado={ocupado}
                onAccion={() => {
                    const v = version.current;
                    if (v === null) {
                        return;
                    }
                    setOcupado(true);
                    ops.discardFloorPlanDraft(negocio, plan.id, v)
                        .then(() => { avisar({ tipo: 'ok', texto: 'Borrador descartado.' }); setDescartar(false); onCambio(); })
                        .catch((e: unknown) => { if (ETag412(e)) { setSuperada(true); setDescartar(false); } else { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido descartar.' }); } })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setDescartar(false); }}
            />
            <Ventana
                abierta={retirar !== null}
                titulo="¿Retirar la versión programada?"
                texto="No llegará a entrar en vigor. Queda en Versiones como retirada."
                accion="Retirar"
                accionIcono="circle-x"
                cancelar="Volver"
                ocupado={ocupado}
                inactiva={(retirar ?? '').trim() === ''}
                onAccion={() => {
                    const id = plan.scheduled_version?.id;
                    if (id === undefined) {
                        return;
                    }
                    setOcupado(true);
                    ops.withdrawFloorPlanVersion(negocio, id, (retirar ?? '').trim())
                        .then(() => { avisar({ tipo: 'ok', texto: 'Versión retirada.' }); setRetirar(null); onCambio(); })
                        .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido retirar.' }); })
                        .finally(() => { setOcupado(false); });
                }}
                onCerrar={() => { setRetirar(null); }}
            >
                <Campo etiqueta="Motivo" valor={retirar ?? ''} onChange={setRetirar} max={500} />
            </Ventana>
            <Ventana
                abierta={nuevaSala !== null}
                titulo="Añadir una sala"
                accion="Añadir"
                accionIcono="plus"
                inactiva={(nuevaSala ?? '').trim() === '' || salas.some((s) => s.name === (nuevaSala ?? '').trim())}
                onAccion={() => {
                    if (contenido === null) {
                        return;
                    }
                    const nombre = (nuevaSala ?? '').trim();
                    cambiar({ ...contenido, rooms: [...contenido.rooms, { name: nombre, width: 1200, height: 800, sort_order: contenido.rooms.length }] });
                    setSala(salas.length);
                    setNuevaSala(null);
                }}
                onCerrar={() => { setNuevaSala(null); }}
            >
                <Campo etiqueta="Nombre de la sala" valor={nuevaSala ?? ''} onChange={setNuevaSala} max={40} />
            </Ventana>
        </>
    );
}

/** Paleta de la decoración (G882): los siete tipos con su muestra; la cabecera «Decoración · N de 2.000» la pliega. */
function Paleta({ cantidad, abierta, dibujandoPared, conRejilla, onAbrir, onAnadir, onRejilla }: { cantidad: number; abierta: boolean; dibujandoPared: boolean; conRejilla: boolean; onAbrir: () => void; onAnadir: (t: TipoPieza) => void; onRejilla: (v: boolean) => void }) {
    const lleno = cantidad >= MAX_PIEZAS;
    return (
        <>
            <button type="button" className="rv-pl-paleta__cab" aria-expanded={abierta} onClick={onAbrir}>
                <span className="rv-rs-subtitulo">Decoración</span>
                <small>{`${miles(cantidad)} de ${miles(MAX_PIEZAS)}`}</small>
                <Icono nombre={abierta ? 'chevron-up' : 'chevron-down'} />
            </button>
            {abierta && (
                <>
                    <div className="rv-pl-paleta">
                        {TIPOS.map((t) => (
                            <button
                                key={t.tipo}
                                type="button"
                                aria-label={`Añadir ${t.nombre.toLowerCase()}${t.tipo === 'wall' ? ' (se dibuja punto a punto)' : ''}`}
                                aria-pressed={t.tipo === 'wall' && dibujandoPared}
                                disabled={lleno}
                                onClick={() => { onAnadir(t.tipo); }}
                            >
                                <MuestraPieza tipo={t.tipo} />
                                <span>{t.nombre}</span>
                            </button>
                        ))}
                    </div>
                    {lleno
                        ? <p className="rv-sl-nota rv-rc-error" role="note">{AVISO_LIMITE}</p>
                        : <p className="rv-sl-nota">Toca una pieza y aparece en el centro de la sala. La pared se dibuja tocando punto a punto. La decoración no se reserva y va siempre debajo de los reservados.</p>}
                    <Interruptor etiqueta="Ajustar a la rejilla" ayuda={`Al mover, de ${String(PASO_REJILLA)} en ${String(PASO_REJILLA)}; al girar, de ${String(PASO_GIRO)}° en ${String(PASO_GIRO)}°`} icono="magnet" valor={conRejilla} onChange={onRejilla} />
                </>
            )}
        </>
    );
}

/**
 * Campo de una medida del plano (X, Y, Ancho, Fondo o Giro): lo escrito se confirma al salir del campo o con Enter
 * (G1208) y entonces pasa la regla de la huella; si no se hace, el campo vuelve a su valor.
 */
function CampoMedida({ etiqueta, valor, min, max, onConfirmar }: { etiqueta: string; valor: number; min: number; max: number; onConfirmar: (v: number) => void }) {
    const [texto, setTexto] = useState<string | null>(null);
    const escrito = useRef<string | null>(null);
    const confirmar = () => {
        const t = escrito.current;
        escrito.current = null;
        setTexto(null);
        if (t === null) {
            return;
        }
        const v = Math.min(max, Math.max(min, Math.round(Number(t) || 0)));
        if (v !== valor) {
            onConfirmar(v);
        }
    };
    return <Campo etiqueta={etiqueta} tipo="number" valor={texto ?? String(valor)} onChange={(v) => { escrito.current = v; setTexto(v); }} onEnter={confirmar} onSalir={confirmar} />;
}

/** Propiedades de la pieza elegida: qué es, nombre o texto (contador de 40), posición, tamaño y giro; la pared, sus puntos. */
function PiezaElegida({ pieza, sala, conRejilla, nombreLocal, puntoElegido, onHecho, onTipo, onNombre, onMedida, onEscribir, onQuitarPunto, onCerrarOAbrir, onDuplicar, onQuitar }: {
    pieza: Decoracion;
    sala: string;
    conRejilla: boolean;
    nombreLocal: string | null;
    puntoElegido: number | null;
    onHecho: () => void;
    onTipo: (t: TipoPieza) => void;
    onNombre: (v: string) => void;
    /** Una medida escrita y confirmada (G1208): pasa la regla de la huella. */
    onMedida: (g: Partial<Decoracion['geometry']>, modo: 'grupo' | 'tamano' | 'giro') => void;
    onEscribir: () => void;
    onQuitarPunto: () => void;
    onCerrarOAbrir: () => void;
    onDuplicar: () => void;
    onQuitar: () => void;
}) {
    const esTexto = pieza.kind === 'text';
    const valor = nombreLocal ?? pieza.label ?? '';
    const pts = puntosDe(pieza);
    const g = pieza.geometry;
    const abiertos = pts === null ? [] : puntosAbiertos(pts);
    const cerrada = pts !== null && estaCerrada(pts);
    const largo = abiertos.reduce((a, p, i) => (i === 0 ? 0 : a + Math.hypot(p[0] - (abiertos[i - 1]?.[0] ?? 0), p[1] - (abiertos[i - 1]?.[1] ?? 0))), 0) + (cerrada && abiertos.length > 0 ? Math.hypot((abiertos[0]?.[0] ?? 0) - (abiertos[abiertos.length - 1]?.[0] ?? 0), (abiertos[0]?.[1] ?? 0) - (abiertos[abiertos.length - 1]?.[1] ?? 0)) : 0);
    return (
        <aside className="rv-pl-lado" aria-label={NOMBRE_TIPO[pieza.kind]}>
            <header className="rv-pl-lado__cab">
                <span><small>{`Decoración • ${sala}`}</small><strong>{NOMBRE_TIPO[pieza.kind]}</strong><small>No se reserva: solo se dibuja. Va siempre debajo de los reservados.</small></span>
                <button type="button" className="rv-b rv-b-sec" onClick={onHecho}><Icono nombre="check" /><span>Hecho</span></button>
            </header>
            <div className="rv-sl-form">
                <Desplegable etiqueta="Qué es" valor={pieza.kind} opciones={TIPOS.map((t) => ({ valor: t.tipo, texto: t.nombre }))} onChange={(v) => { onTipo(v as TipoPieza); }} />
                <Campo
                    etiqueta={esTexto ? 'Texto' : 'Nombre (opcional)'}
                    valor={valor}
                    onChange={onNombre}
                    error={errorDeNombre(valor, esTexto)}
                    ayuda={`${String(valor.length)} de ${String(MAX_TEXTO)}${esTexto ? ' · la letra sigue al rectángulo' : ` · por ejemplo, «${EJEMPLO_NOMBRE[pieza.kind]}»`}`}
                />
                {esTexto && <button type="button" className="rv-b rv-b-sec" onClick={onEscribir}><Icono nombre="type" /><span>Escribir en el plano</span></button>}
                {pts === null ? (
                    <>
                        <div className="rv-rc-dos rv-pl-par">
                            <CampoMedida etiqueta="Posición X" valor={g.x ?? 0} min={0} max={10000} onConfirmar={(v) => { onMedida({ x: v }, 'grupo'); }} />
                            <CampoMedida etiqueta="Posición Y" valor={g.y ?? 0} min={0} max={10000} onConfirmar={(v) => { onMedida({ y: v }, 'grupo'); }} />
                        </div>
                        <div className="rv-rc-dos rv-pl-par">
                            <CampoMedida etiqueta="Ancho" valor={g.width ?? 0} min={1} max={10000} onConfirmar={(v) => { onMedida({ width: v }, 'tamano'); }} />
                            <CampoMedida etiqueta="Fondo" valor={g.height ?? 0} min={1} max={10000} onConfirmar={(v) => { onMedida({ height: v }, 'tamano'); }} />
                        </div>
                        <div className="rv-rc-dos rv-pl-par">
                            <CampoMedida etiqueta="Giro (grados)" valor={g.rotation ?? 0} min={0} max={359} onConfirmar={(v) => { onMedida({ rotation: v }, 'giro'); }} />
                            <button type="button" className="rv-b rv-b-sec" onClick={() => { onMedida({ rotation: grados((g.rotation ?? 0) + PASO_GIRO) }, 'giro'); }}><Icono nombre="rotate-cw" /><span>{`Girar ${String(PASO_GIRO)}°`}</span></button>
                        </div>
                        <p className="rv-sl-nota">{`Arrástrala para moverla. El asa de arriba la gira${conRejilla ? ` de ${String(PASO_GIRO)} en ${String(PASO_GIRO)}` : ''}.`}</p>
                    </>
                ) : (
                    <>
                        <dl className="rv-rs-presupuesto">
                            <div><dt>Puntos</dt><dd>{textoPuntos(abiertos.length, cerrada)}</dd></div>
                            <div><dt>Largo en la rejilla</dt><dd>{miles(Math.round(largo))}</dd></div>
                            <div><dt>Forma</dt><dd>{cerrada ? 'Cerrada' : 'Abierta'}</dd></div>
                        </dl>
                        <p className="rv-sl-nota">{notaPared(abiertos.length, cerrada, puntoElegido)}</p>
                        <button type="button" className="rv-b rv-b-sec" disabled={puntoElegido === null || abiertos.length <= 2} onClick={onQuitarPunto}>
                            <Icono nombre="minus" /><span>{puntoElegido === null ? 'Quitar un punto' : `Quitar el punto ${String(puntoElegido + 1)}`}</span>
                        </button>
                        <button type="button" className="rv-b rv-b-sec" disabled={!cerrada && !sePuedeCerrar(abiertos.length)} onClick={onCerrarOAbrir}>
                            <Icono nombre={cerrada ? 'minus' : 'square'} /><span>{cerrada ? 'Abrir la pared' : 'Cerrar la pared'}</span>
                        </button>
                    </>
                )}
                <button type="button" className="rv-b rv-b-sec" onClick={onDuplicar}><Icono nombre="copy" /><span>Duplicar</span></button>
                <button type="button" className="rv-b rv-b-sec rv-b-rojo" onClick={onQuitar}><Icono nombre="trash-2" /><span>{pts === null ? 'Quitar la pieza' : 'Quitar la pared'}</span></button>
            </div>
        </aside>
    );
}

type Cuando = 'proxima' | 'fecha' | 'ahora';

function Publicar({ ops, negocio, movil, plan, borrador, contexto, onCerrar }: { ops: Operaciones; negocio: string; movil: boolean; plan: PlanoApi; borrador: ResumenVersion; contexto: ContextoReservas; onCerrar: (hecho: ResumenVersion | null) => void }) {
    const avisar = useAvisoBreve();
    const [cuando, setCuando] = useState<Cuando>('proxima');
    const [dia, setDia] = useState(() => sumarDias(hoyEn(contexto.zona), 1));
    const [impacto, setImpacto] = useState<{ clave: string; datos: ImpactoPlano | null; error: string | null } | null>(null);
    const [nota, setNota] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [intento, setIntento] = useState(0);
    const [deco, setDeco] = useState<CambiosDecoracion | null>(null);
    const activaId = plan.effective_version?.id;
    const fecha: FechaDeEfecto = cuando === 'ahora' ? { immediate: true } : cuando === 'fecha' ? { immediate: false, effective_local_date: dia } : { immediate: false };
    const clave = `${cuando}|${cuando === 'fecha' ? dia : ''}|${String(intento)}`;

    useEffect(() => {
        let vivo = true;
        ops.previewFloorPlanImpact(negocio, plan.id, cuando === 'ahora' ? { immediate: true } : cuando === 'fecha' ? { immediate: false, effective_local_date: dia } : { immediate: false })
            .then((d) => { if (vivo) { setImpacto({ clave, datos: d, error: null }); } })
            .catch((e: unknown) => { if (vivo) { setImpacto({ clave, datos: null, error: avisoBreveDeError(e) ?? (e instanceof ErrorApi ? e.message : 'No se ha podido revisar el impacto.') }); } });
        return () => { vivo = false; };
    }, [ops, negocio, plan.id, cuando, dia, clave]);

    // La decoración no viene en `FloorPlanImpact`: se compara la del borrador con la de la versión activa.
    // G1179 (ADR-0268): emparejado por sala, tipo y geometría (`cambiosDeDecoracion`).
    useEffect(() => {
        let vivo = true;
        Promise.all([ops.getFloorPlanDraft(negocio, plan.id), activaId === undefined ? Promise.resolve(null) : ops.getFloorPlanVersion(negocio, activaId)])
            .then(([b, a]) => { if (vivo) { setDeco(cambiosDeDecoracion(a?.content.decorations ?? [], b.content.decorations ?? [])); } })
            .catch(() => { if (vivo) { setDeco(null); } });
        return () => { vivo = false; };
    }, [ops, negocio, plan.id, activaId, intento]);

    const actual = impacto?.clave === clave ? impacto : null;
    const d = actual?.datos ?? null;
    const n = (x: number | undefined) => String(x ?? 0);
    // «Mesas colocadas», «Reservados colocados» (G214, con el género de la palabra del negocio, como el dibujo 8.6).
    const plural = `${mayuscula(contexto.palabra)}s`;
    const femenina = conArticulo(contexto.palabra).startsWith('la ');
    // El borrador igual que la versión activa (con la decoración, G1179): no hay nada que publicar.
    const sinCambios = d !== null && deco !== null && (d.changes.rooms_added ?? 0) + (d.changes.rooms_removed ?? 0) + (d.changes.resources_placed ?? 0) + (d.changes.resources_removed ?? 0) + (d.changes.resource_changes ?? 0) + deco.nuevas + deco.cambiadas + deco.quitadas === 0;
    const proxima = cuando === 'proxima' && d?.effective_local_date != null ? `${diaCorto(d.effective_local_date)} • recomendado` : 'Recomendado';
    return (
        <PanelLateral
            abierto
            movil={movil}
            onCerrar={() => { onCerrar(null); }}
            icono="send"
            eyebrow="Revisar el impacto"
            titulo={`Publicar la versión ${String(borrador.number)}`}
            subtitulo={plan.effective_version == null ? 'Primera versión del plano' : `Sobre la versión ${String(plan.effective_version.number)}`}
            accion="Publicar"
            accionIcono="send"
            ocupado={ocupado}
            ocupadoTexto="Publicando…"
            inactiva={d === null || sinCambios}
            onAccion={() => {
                if (d === null) {
                    return;
                }
                setOcupado(true);
                ops.publishFloorPlanDraft(negocio, plan.id, borrador, { ...fecha, impact_token: d.impact_token, ...(nota.trim() === '' ? {} : { note: nota.trim() }) })
                    .then((v) => { onCerrar(v); })
                    .catch((e: unknown) => {
                        if (e instanceof ErrorApi && (e.estado === 409 || e.estado === 412)) {
                            avisar({ tipo: 'error', texto: 'El borrador o las reservas han cambiado: revisa el impacto otra vez.' });
                            setIntento((i) => i + 1);
                        } else {
                            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido publicar.' });
                        }
                    })
                    .finally(() => { setOcupado(false); });
            }}
        >
            <div className="rv-sl-form">
                <h3 className="rv-rs-subtitulo">Qué cambia</h3>
                {actual === null ? <p className="rv-sl-nota">Revisando…</p> : actual.error !== null ? <p className="rv-sl-nota rv-rc-error" role="alert">{actual.error}</p> : d !== null && (
                    <dl className="rv-rs-presupuesto">
                        <div><dt>Salas nuevas</dt><dd>{n(d.changes.rooms_added)}</dd></div>
                        <div><dt>Salas quitadas</dt><dd>{n(d.changes.rooms_removed)}</dd></div>
                        <div><dt>{`${plural} ${femenina ? 'colocadas' : 'colocados'}`}</dt><dd>{n(d.changes.resources_placed)}</dd></div>
                        <div><dt>{`${plural} ${femenina ? 'quitadas' : 'quitados'} del plano`}</dt><dd>{n(d.changes.resources_removed)}</dd></div>
                        <div><dt>Cambios en Recursos</dt><dd>{n(d.changes.resource_changes)}</dd></div>
                        {deco !== null && <div><dt>Decoración</dt><dd>{String(deco.nuevas + deco.cambiadas + deco.quitadas)}</dd></div>}
                    </dl>
                )}
                {d !== null && actual?.error == null && deco !== null && deco.nuevas + deco.cambiadas + deco.quitadas > 0 && (
                    <ul className="rv-pl-detalle">
                        {lineasDeCambios(deco).map((l) => <li key={l}>{l}</li>)}
                        {(d.changes.rooms_added ?? 0) + (d.changes.rooms_removed ?? 0) + (d.changes.resources_placed ?? 0) + (d.changes.resources_removed ?? 0) + (d.changes.resource_changes ?? 0) === 0 && <li>{SOLO_DECORACION}</li>}
                    </ul>
                )}
                {sinCambios && <p className="rv-sl-nota" role="status">El borrador es igual que la versión activa: no hay nada que publicar.</p>}
                <h3 className="rv-rs-subtitulo">Cuándo entra en vigor</h3>
                <div role="radiogroup" aria-label="Cuándo entra en vigor" className="rv-pl-cuando">
                    <OpcionRadio titulo="Próxima jornada sin servicio" texto={proxima} elegida={cuando === 'proxima'} onElegir={() => { setCuando('proxima'); }} />
                    <OpcionRadio titulo="Otra fecha" texto="Eliges el día" elegida={cuando === 'fecha'} onElegir={() => { setCuando('fecha'); }} />
                    {cuando === 'fecha' && <Fecha etiqueta="Día" valor={dia} min={hoyEn(contexto.zona)} onChange={setDia} />}
                    <OpcionRadio titulo="Ahora mismo" texto="Aunque haya servicio: lo ya sentado conserva su sitio" elegida={cuando === 'ahora'} onElegir={() => { setCuando('ahora'); }} />
                </div>
                {cuando === 'ahora' && d?.open_service === true && <p className="rv-sl-nota rv-rc-error" role="note">Hay servicio ahora mismo. Lo ya sentado conserva su sitio; las reservas que pierdan el suyo quedan para recolocar en Sala.</p>}
                <h3 className="rv-rs-subtitulo">Reservas futuras afectadas</h3>
                {d === null ? null : d.affected_reservations.length === 0 ? (
                    <p className="rv-sl-nota">Ninguna reserva pierde su sitio.</p>
                ) : (
                    <>
                        <p className="rv-sl-nota">{`${String(d.affected_reservations.length)} ${d.affected_reservations.length === 1 ? 'reserva pierde su sitio y queda' : 'reservas pierden su sitio y quedan'} «Para recolocar» en la Sala de su día.`}</p>
                        <ul className="rv-sl-desplazadas">
                            {d.affected_reservations.map((a) => (
                                <li key={a.reservation_id}>
                                    <span>{`${diaCorto(hoyEn(contexto.zona, new Date(a.starts_at)))}, ${horaEn(a.starts_at, contexto.zona)}`}</span>
                                    <strong>{`${String(a.party_size ?? 0)} personas • ${(a.resource_ids ?? []).map((id) => contexto.recursos.find((r) => r.id === id)?.code ?? '').join(' + ')}`}</strong>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                <Campo etiqueta="Qué cambia (opcional)" valor={nota} onChange={setNota} max={500} ayuda="Se guarda con la versión." />
            </div>
        </PanelLateral>
    );
}

function Versiones({ ops, negocio, movil, plan, contexto, edita, onCambio }: { ops: Operaciones; negocio: string; movil: boolean; plan: PlanoApi; contexto: ContextoReservas; edita: boolean; onCambio: () => void }) {
    const avisar = useAvisoBreve();
    const [lista, setLista] = useState<ResumenVersion[] | null>(null);
    const [abierta, setAbierta] = useState<ResumenVersion | null>(null);
    const [motivo, setMotivo] = useState('');
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.listFloorPlanVersions(negocio, plan.id, { limit: 100 })
            .then((l) => { if (vivo) { setLista(l.data.filter((v) => v.status !== 'draft').sort((a, b) => b.number - a.number)); } })
            .catch(() => { if (vivo) { setLista([]); } });
        return () => { vivo = false; };
    }, [ops, negocio, plan.id]);

    if (lista === null) {
        return <EstadoCargando seccion="Versiones" />;
    }
    const enVigor = (v: ResumenVersion) => (v.status === 'withdrawn' ? 'Retirada antes de entrar' : v.effective_local_date == null ? '—' : diaCorto(v.effective_local_date));
    return (
        <>
            <Tabla
                etiqueta="Versiones"
                movil={movil}
                columnas={[
                    { clave: 'version', titulo: 'Versión', ancho: 'minmax(0, 1.6fr)' },
                    { clave: 'vigor', titulo: 'En vigor' },
                    { clave: 'publicada', titulo: 'Publicada' },
                    { clave: 'estado', titulo: 'Estado' },
                ]}
                vacia={<p className="rv-sl-nota rv-sl-nota--centro">Aún no has publicado ninguna versión.</p>}
                filas={lista.map((v) => {
                    const [texto, chip] = ESTADO_VERSION[v.status];
                    return {
                        clave: v.id,
                        etiqueta: `Versión ${String(v.number)}`,
                        onAbrir: () => { setAbierta(v); setMotivo(''); },
                        celdas: {
                            version: <strong>{`Versión ${String(v.number)}`}</strong>,
                            vigor: enVigor(v),
                            publicada: v.published_at == null ? '—' : `${diaCorto(hoyEn(contexto.zona, new Date(v.published_at)))}, ${horaEn(v.published_at, contexto.zona)}`,
                            estado: <Chip estado={chip}>{texto}</Chip>,
                        },
                    };
                })}
            />
            <p className="rv-sl-nota"><Icono nombre="lock" />{' Las versiones no se borran ni se cambian. Para volver a una, se abre un borrador con su dibujo y se publica como versión nueva.'}</p>
            {abierta !== null && (
                <PanelLateral
                    abierto
                    movil={movil}
                    onCerrar={() => { setAbierta(null); }}
                    icono="history"
                    eyebrow="Versión del plano"
                    titulo={`Versión ${String(abierta.number)}`}
                    chip={ESTADO_VERSION[abierta.status][0]}
                    chipEstado={ESTADO_VERSION[abierta.status][1]}
                    {...(edita && abierta.status !== 'withdrawn' && abierta.status !== 'active' ? {
                        accion: 'Volver a esta versión',
                        accionIcono: 'history' as const,
                        ocupado,
                        onAccion: () => {
                            setOcupado(true);
                            ops.openFloorPlanDraft(negocio, plan.id, abierta.id)
                                .then(() => { avisar({ tipo: 'ok', texto: `Borrador abierto con el dibujo de la versión ${String(abierta.number)}.` }); setAbierta(null); onCambio(); })
                                .catch((e: unknown) => {
                                    avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.estado === 409 ? 'Ya hay un borrador abierto: publícalo o descártalo antes.' : avisoBreveDeError(e) ?? 'No se ha podido abrir el borrador.' });
                                })
                                .finally(() => { setOcupado(false); });
                        },
                    } : {})}
                >
                    <div className="rv-sl-form">
                        <dl className="rv-rs-presupuesto">
                            <div><dt>En vigor</dt><dd>{enVigor(abierta)}</dd></div>
                            <div><dt>Publicada</dt><dd>{abierta.published_at == null ? '—' : `${diaCorto(hoyEn(contexto.zona, new Date(abierta.published_at)))}, ${horaEn(abierta.published_at, contexto.zona)}`}</dd></div>
                            {(abierta.displaced_reservations ?? 0) > 0 && <div><dt>Reservas que perdieron su sitio</dt><dd>{abierta.displaced_reservations}</dd></div>}
                        </dl>
                        {edita && abierta.status === 'scheduled' && (
                            <>
                                <Campo etiqueta="Motivo para retirarla" valor={motivo} onChange={setMotivo} max={500} />
                                <button
                                    type="button"
                                    className="rv-b rv-b-sec rv-b-rojo"
                                    disabled={motivo.trim() === '' || ocupado}
                                    onClick={() => {
                                        setOcupado(true);
                                        ops.withdrawFloorPlanVersion(negocio, abierta.id, motivo.trim())
                                            .then(() => { avisar({ tipo: 'ok', texto: 'Versión retirada.' }); setAbierta(null); onCambio(); })
                                            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido retirar.' }); })
                                            .finally(() => { setOcupado(false); });
                                    }}
                                >
                                    <Icono nombre="circle-x" /><span>Retirar</span>
                                </button>
                            </>
                        )}
                    </div>
                </PanelLateral>
            )}
        </>
    );
}
